import { Button } from "@mui/material";
import { updateDisbursement } from "apis/controllers/disbursements";
import { FileProvider, useFileContext } from "context/FilesV2";
import { useLoading } from "hooks/useLoading";
import ConfirmDialog from "qubCommon/Dialogs/ConfirmationDialog";
import FilePreviewer from "qubCommon/FilePreviewer";
import { formatFloat } from "qubCommon/wijmoHelpers/numberMethods";
import { useEffect, useRef, useState } from "react";
import { FormProvider } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import {
  deleteBundle,
  deleteFile,
  downloadFile,
  formatFiles,
  uploadFiles,
} from "util";
import "./DisbursementDetails.css";
import AlertBanner from "./Components/AlertBanner";
import Amendment from "./Components/Amendment";
import DisbursementActions from "./Components/DisbursementActions";
import Attachments from "./Components/Attachments";
import History from "./Components/History";
import Institute from "./Components/Institute";
import LegalRepresentative from "./Components/LegalRepresentative";
import ListFileItem from "./Components/ListFileItem";
import ScientificDirector from "./Components/ScientificDirector";
import SummaryCard from "./Components/SummaryCard";
import useDisbursementDetails from "./useDisbursementDetails";

const DisbursementDetails = () => {
  const { form, data, succeeded, files, fetchDisbursement, resetForm } =
    useDisbursementDetails();

  const { id } = useParams();

  if (succeeded)
    return (
      <div className="disbursements-container">
        <FileProvider
          downloadFile={(fileId, type) => downloadFile(fileId, type)}
          uploadFiles={(fileGroups) => uploadFiles(fileGroups, null)}
          onDeleteBundle={async (bundleId) =>
            await deleteBundle(bundleId, 13, null, id, null)
          }
          onDeleteFile={async (fileId) => {
            await deleteFile(fileId, 13, null, id, null);
          }}
        >
          <Form
            form={form}
            data={data}
            files={files}
            fetchDisbursement={fetchDisbursement}
            resetForm={resetForm}
          />
        </FileProvider>
      </div>
    );
};

const Form = (props) => {
  const { form, data, files, fetchDisbursement, resetForm } = props;
  const [openAmendment, setOpenAmendment] = useState(false);
  const [fileToPreview, setFileToPreview] = useState(null);
  const [errorBanner, setErrorBanner] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogProps, setDialogProps] = useState({
    title: "",
    content: "",
    onConfirm: () => {},
  });
  const { id } = useParams();
  const { fileGroups, control } = useFileContext();
  const fgRefs = useRef([]);
  const { setLoading } = useLoading();
  const settings = data?.disbursement?.settings;
  const navigate = useNavigate();

  useEffect(() => {
    try {
      const formattedFiles = formatFiles(files);
      if (formattedFiles !== undefined) {
        control.initialize(formattedFiles);
      } else {
        control.initialize([]);
      }
    } catch (error) {
      console.error("error", error);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [files]);

  const prepareBundles = () => {
    const bundleIds = fileGroups.reduce((acc, group) => {
      return [
        ...acc,
        ...group.bundles
          .filter((bundle) => bundle?.files?.length > 0)
          .map((bundle) => bundle.id),
      ];
    }, []);

    return { bundleIds };
  };

  const handleDisbursementUpdate = async (
    action,
    additionalData = {},
    uploadFiles = true
  ) => {
    try {
      setLoading(true);
      const disbursementData = action !== "approve" ? form.getValues() : { id };
      if (uploadFiles) {
        const resFiles = await control.uploadFileGroups();
        if (resFiles?.status === "error") {
          setErrorBanner(resFiles?.message);
          setLoading(false);
          return;
        }
      }

      const uploadData = {
        ...disbursementData,
        ...additionalData,
        action,
      };

      const res = await updateDisbursement(uploadData);

      if (res?.status === "success") await fetchDisbursement();
      if (res?.status === "error") {
        setErrorBanner(res?.message);
        if (action === "submit") resetForm();
      }

      setLoading(false);
      setOpenDialog(false);
    } catch (e) {
      console.log(e);
      setLoading(false);
      setErrorBanner("<div>Προέκυψε Σφάλμα!</div>");
    }
  };

  const handleOpenDialog = (actionType) => {
    switch (actionType) {
      case "accept":
        setDialogProps({
          title: "Αποδοχή Τροποποιήσεων",
          content: "Θέλετε σίγουρα να αποδεχτείτε τις τροποποιήσεις;",
          onConfirm: handleAccept,
        });
        break;
      case "refuse":
        setDialogProps({
          title: "Απόρριψη Τροποποιήσεων",
          content: "Θέλετε σίγουρα να απορρίψετε τις τροποποιήσεις;",
          onConfirm: handleRefuse,
        });
        break;
      case "approve":
        setDialogProps({
          title: "Έγκριση Αίτησης",
          content: "Θέλετε σίγουρα να εγκρίνετε την αίτηση;",
          onConfirm: handleApprove,
          commentsOn: settings?.approve?.commentsOn,
          withFileUpload: settings?.approve?.withFileUpload,
          fileDatakey: settings?.approve?.fileDataKey,
          fileDataValue: settings?.approve?.fileDataValue,
        });
        break;
      case "reject":
        setDialogProps({
          title: "Απόρριψη Αίτησης",
          content: ` Θέλετε σίγουρα να επιστρέψετε την αίτηση στον αιτούντα; 
            Παρακαλώ αναφέρετε τους λόγους απόρριψης.`,
          onConfirm: settings?.reject?.withFileUpload
            ? handleRejectWithFileUpload
            : handleReject,
          commentsOn: true,
          withFileUpload: settings?.reject?.withFileUpload,
          fileDatakey: settings?.reject?.fileDataKey,
          fileDataValue: settings?.reject?.fileDataValue,
        });
        break;
      case "submit":
        setDialogProps({
          title: "Υποβολή Αίτησης",
          content: "Θέλετε σίγουρα να υποβάλλετε την αίτηση;",
          onConfirm: handleSubmit,
          withFileUpload: settings?.submit?.withFileUpload,
          fileDatakey: settings?.submit?.fileDataKey,
          fileDataValue: settings?.submit?.fileDataValue,
        });
        break;
      case "cancel":
        setDialogProps({
          title: "Ακύρωση Αίτησης",
          content: "Θέλετε σίγουρα να ακυρώσετε την αίτηση;",
          onConfirm: handleCancel,
        });
        break;
      case "backToHome":
        const dirtyFields = Object.keys(form.formState.dirtyFields).map(
          (x) => ({
            field: x,
            value: form.getValues(x),
          })
        );
        if (!!dirtyFields?.length || control.isDirty)
          setDialogProps({
            title: "Προσοχή!",
            content:
              "Έχετε πραγματοποιήσει αλλαγές στη φόρμα σας. Αν αποχωρήσετε οι αλλαγές θα χαθούν. Θέλετε να κλείσετε τη σελίδα;",
            onConfirm: () => navigate("/govnr/disbursements"),
          });
        else navigate("/govnr/disbursements");
        break;
      case "partiallyApprove":
        setDialogProps({
          title: settings?.partiallyApprove?.partiallyApproveBtnTxt,
          content:
            "Θέλετε σίγουρα να προχωρήσετε σε αλλαγή κατάστασης της αίτησης;",
          onConfirm: handlePartiallyApprove,
          withFileUpload: settings?.partiallyApprove?.withFileUpload,
          fileDatakey: settings?.partiallyApprove?.fileDataKey,
          fileDataValue: settings?.partiallyApprove?.fileDataValue,
        });
        break;
      default:
        return;
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = async () => {
    await setOpenDialog(false);
    await setDialogProps((prev) => ({ ...prev, withFileUpload: false }));
  };

  const handlePreviewFile = async (file) => {
    const res = await downloadFile(file?.id, file?.type ?? file?.contentType);
    setFileToPreview(res);
  };

  // Refactored handlers

  const handleSave = async () => {
    const { bundleIds } = prepareBundles();
    await handleDisbursementUpdate("save", {
      bundles: bundleIds,
    });
  };

  const handleAccept = async () => {
    await handleDisbursementUpdate(
      "accept",
      { id: form.getValues("id") },
      false
    );
  };

  const handleRefuse = async () => {
    await handleDisbursementUpdate(
      "refuse",
      { id: form.getValues("id") },
      false
    );
  };

  const handleApproveSave = async () => {
    try {
      setLoading(true);

      // ToDo SOS Uncomment this
      const resFiles = await control.uploadFileGroups();
      if (resFiles?.status === "error") {
        setErrorBanner(resFiles?.message);
        setLoading(false);
        return;
      }
      await fetchDisbursement();

      setLoading(false);
    } catch {
      setLoading(false);
      setErrorBanner("<div>Προέκυψε σφάλμα!</div>");
    }
  };

  const handleApprove = async (fileBundles = [], comments) => {
    await handleDisbursementUpdate(
      "approve",

      fileBundles?.length > 0
        ? { bundles: fileBundles, comments }
        : { comments },
      true
    );
  };

  const handleRejectWithFileUpload = async (fileBundles = [], comments) => {
    const { bundleIds } = prepareBundles();
    const additionalData =
      fileBundles?.length > 0
        ? {
            id: form.getValues("id"),
            action: "reject",
            comments,
            bundles: fileBundles,
          }
        : {
            id: form.getValues("id"),
            action: "reject",
            comments,
          };
    await handleDisbursementUpdate("reject", additionalData, false);
  };

  const handleReject = async (comments) => {
    await handleDisbursementUpdate(
      "reject",
      {
        id: form.getValues("id"),
        comments,
      },
      true
    );
  };

  const handleSubmit = async (fileBundles = []) => {
    const { bundleIds } = prepareBundles();
    let submitBundles = bundleIds;
    if (fileBundles?.length > 0) submitBundles = [...bundleIds, ...fileBundles];
    await handleDisbursementUpdate(
      "submit",
      submitBundles?.length > 0
        ? {
            bundles: submitBundles,
          }
        : {}
    );
  };

  const handleCancel = async () => {
    await handleDisbursementUpdate(
      "cancel",
      { id: form.getValues("id") },
      false
    );
  };

  const handlePartiallyApprove = async (fileBundles = []) => {
    await handleDisbursementUpdate(
      "partially_approve",
      fileBundles?.length > 0
        ? {
            bundles: fileBundles,
          }
        : {},
      false
    );
  };

  return (
    <FormProvider {...form}>
      <FilePreviewer
        file={fileToPreview}
        onClose={() => setFileToPreview(null)}
      />
      <ConfirmDialog
        projectTableId={13}
        open={openDialog}
        title={dialogProps.title}
        content={dialogProps.content}
        onClose={handleCloseDialog}
        onConfirm={dialogProps.onConfirm}
        commentsOn={dialogProps.commentsOn}
        withFileUpload={dialogProps?.withFileUpload}
        fileDatakey={dialogProps?.fileDatakey}
        fileDataValue={dialogProps?.fileDataValue}
      />
      <AlertBanner canClose={false} info={data?.disbursement?.status}>
        <div style={{ marginTop: 4, marginBottom: 4 }}>
          {data?.disbursement?.status?.files.map((file, index) => (
            <ListFileItem
              key={index}
              file={file}
              index={index}
              handlePreviewFile={() => handlePreviewFile(file)}
            />
          ))}
        </div>
        <div className="buttons-container">
          {data?.disbursement?.status?.refuseBtnText && (
            <Button
              variant="contained"
              color="error"
              onClick={() => handleOpenDialog("refuse")}
            >
              {data?.disbursement?.status?.refuseBtnText}
            </Button>
          )}
          {data?.disbursement?.status?.acceptBtnText && (
            <Button
              variant="contained"
              onClick={() => handleOpenDialog("accept")}
            >
              {data?.disbursement?.status?.acceptBtnText}
            </Button>
          )}
        </div>
      </AlertBanner>
      <SummaryCard
        protocolNumber={data?.disbursement?.protocolNumber}
        protocolNumber2={data?.disbursement?.protocolNumber2}
        status={data?.disbursement?.status}
        created={data?.disbursement?.created}
        updated={data?.disbursement?.updated}
        submitted={data?.disbursement?.submitted}
        installmentAmount={data?.disbursement?.installmentAmount}
        totalAmount={data?.disbursement?.totalAmount}
        type={data?.disbursement?.type}
      />
      <Institute institute={data?.disbursement?.hq} type="hq" />
      {data?.disbursement?.additionalInstitutes?.map((institute, index) => {
        return (
          <Institute
            key={institute?.name + index}
            institute={institute}
            index={index}
            type="additional"
          />
        );
      })}
      <Institute institute={data?.disbursement?.branch} type="branch" />
      <LegalRepresentative rep={data?.disbursement?.legalRepresentative} />

      <Attachments fileGroups={fileGroups} settings={settings?.attachments} />
      <DisbursementActions
        handleCancel={() => handleOpenDialog("cancel")}
        canEdit={settings?.canEdit}
        canCancel={settings?.cancel?.can}
        canSubmit={settings?.submit?.can}
        settings={settings}
        handleSubmit={() => handleOpenDialog("submit")}
        handleApprove={() => handleOpenDialog("approve")}
        handleSave={() => handleSave()}
        handleApproveSave={() => handleApproveSave()}
        handleReject={() => handleOpenDialog("reject")}
        handlePartiallyApprove={() => handleOpenDialog("partiallyApprove")}
        handleAmendment={() => setOpenAmendment(true)}
        handleBackToHome={() => handleOpenDialog("backToHome")}
      />
      <AlertBanner
        openInfo={!!errorBanner}
        setOpenMessage={setErrorBanner}
        info={{
          message: `<div>${errorBanner}</div>`,
          cssClass: "error",
        }}
      />
      <History
        history={data?.disbursement?.history}
        handlePreviewFile={handlePreviewFile}
      />
      <Amendment
        open={openAmendment}
        handleClose={() => setOpenAmendment(false)}
      />
    </FormProvider>
  );
};

export default DisbursementDetails;
